import { Button, Card, Col, Form, Input, Row, Typography, message } from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AotvLogo } from '../components/AotvLogo';
import { PageHead } from '../components/PageHead';
import { api } from '../lib/api';
import { useAuth, type Session } from '../lib/auth';

const { Title, Text, Link } = Typography;

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 14 },
};
const tailLayout = {
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 16, offset: 6 },
    md: { span: 16, offset: 6 },
  },
};

type FormValues = { email: string; password?: string };

export function LoginScene() {
  const navigate = useNavigate();
  const { setSession } = useAuth();

  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [softReject, setSoftReject] = useState<string | null>(null);
  const [devLink, setDevLink] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(true);
  const [form] = Form.useForm<FormValues>();

  const onFinish = async (values: FormValues) => {
    const email = values.email.toLowerCase().trim();
    const password = values.password?.trim();
    setLoading(true);

    try {
      if (password) {
        const res = await api<{ token: string; email: string; role: 'team' | 'speaker' }>(
          '/api/auth/password',
          {
            method: 'POST',
            body: JSON.stringify({ email, password }),
          },
        );
        const session: Session = { token: res.token, email: res.email, role: res.role };
        setSession(session);
        navigate(res.role === 'team' ? '/events' : '/me', { replace: true });
      } else {
        const res = await api<{
          ok: boolean;
          registered?: boolean;
          message?: string;
          link?: string;
        }>('/api/auth/magic-link', {
          method: 'POST',
          body: JSON.stringify({ email }),
        });
        if (res.registered === false) {
          setSoftReject(
            res.message ??
              "We couldn't find that email on AOTV Call. Please contact info@aotv.co.uk for help.",
          );
        } else {
          setSent(true);
          if (res.link) setDevLink(res.link);
        }
      }
    } catch (err) {
      const e = err as { error?: string; status?: number };
      if (password && e.status === 401) {
        message.error('Invalid email or password');
      } else {
        message.error(e.error ?? 'Sign-in failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <PageHead title="Login" />
      <Row justify="center">
        <Col xs={22} sm={20} md={14} lg={12} xl={10} xxl={8}>
          <div className="auth-logo">
            <AotvLogo />
          </div>
          <Card>
            <div style={{ marginBottom: 24 }}>
              <Title level={3} style={{ marginBottom: 4 }}>
                Log in
              </Title>
              <Text type="secondary">
                Enter your email and password.
                {showPassword && (
                  <>
                    {' '}
                    <Link onClick={() => setShowPassword(false)}>
                      Forgot your password? Get an email link instead
                    </Link>
                  </>
                )}
              </Text>
            </div>
            {softReject && !showPassword ? (
              <div style={{ padding: '16px 0' }}>
                <Title level={5}>We couldn't find that email</Title>
                <Text>{softReject}</Text>
                <div style={{ marginTop: 16 }}>
                  <Button
                    type="link"
                    onClick={() => setSoftReject(null)}
                    style={{ paddingLeft: 0 }}
                  >
                    Try a different email
                  </Button>
                </div>
              </div>
            ) : sent && !showPassword ? (
              <div style={{ padding: '16px 0' }}>
                <Title level={5}>Check your email</Title>
                <Text type="secondary">
                  We've sent a sign-in link to your inbox. It will expire in 15
                  minutes. If it doesn't appear, check your spam folder, then
                  contact <a href="mailto:info@aotv.co.uk">info@aotv.co.uk</a>.
                </Text>
                {devLink && (
                  <div
                    style={{
                      marginTop: 24,
                      padding: 12,
                      background: '#fff7e6',
                      border: '1px solid #ffd591',
                      borderRadius: 6,
                    }}
                  >
                    <Text strong>Dev mode:</Text>{' '}
                    <a href={devLink}>Click here to sign in</a>
                  </div>
                )}
              </div>
            ) : (
              <Form {...layout} form={form} name="login" onFinish={onFinish}>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    { required: true, message: 'Please enter your email' },
                    { type: 'email', message: 'Please enter a valid email' },
                  ]}
                >
                  <Input disabled={loading} autoComplete="email" autoFocus />
                </Form.Item>
                {showPassword && (
                  <Form.Item label="Password" name="password">
                    <Input.Password
                      disabled={loading}
                      autoCapitalize="off"
                      placeholder="Optional — leave blank to email a link"
                    />
                  </Form.Item>
                )}
                <Form.Item {...tailLayout}>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    {showPassword ? 'Sign in' : 'Send sign-in link'}
                  </Button>
                  {!showPassword && (
                    <Link
                      style={{ marginLeft: 12 }}
                      onClick={() => setShowPassword(true)}
                    >
                      Use password instead
                    </Link>
                  )}
                </Form.Item>
              </Form>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
